import { useQueryClient } from '@tanstack/react-query'

import { adminQuotingQuotes, partnersQuotes } from 'rails_routes'
import { Modal } from 'slab'
import {
  getGetQuotingProspectsProspectIdQuotesIdGenerationOptionsQueryKey as getQuoteGenerationOptionsQueryKey,
  getGetQuotingProspectsProspectIdQuotesQueryKey as getQuotesQueryKey,
  getQuotingProspectsProspectIdQuotesIdGenerationOptions as getQuoteGenerationOptions,
  usePostQuotingProspectsProspectIdQuotesIdGenerate as useGenerateQuote,
} from 'utils/web_api'

import { type WorkflowType } from './QuoteExplorer'

interface OutdatedConfigModalProps {
  quoteId: string
  prospectId: string
  quoteYear: number
  onNavigate: (url: string) => void
  isOpen: boolean
  workflowType?: WorkflowType
}

export function OutdatedConfigModal ({
  quoteId,
  prospectId,
  quoteYear,
  onNavigate,
  isOpen,
  workflowType = 'sales',
}: OutdatedConfigModalProps) {
  const queryClient = useQueryClient()
  const generateQuoteMutation = useGenerateQuote()

  const handleRegenerateQuote = async () => {
    try {
      const generationOptions = await queryClient.fetchQuery({
        queryKey: getQuoteGenerationOptionsQueryKey(prospectId, quoteId),
        queryFn: () => getQuoteGenerationOptions(prospectId, quoteId)
      })

      await generateQuoteMutation.mutateAsync({
        prospectId,
        id: quoteId,
        data: {
          coverage_percentiles: generationOptions.coverage_percentiles,
          coverage_start_date: generationOptions.coverage_start_date,
          metal_tiers: generationOptions.metal_tiers,
          year: generationOptions.quote_year,
        }
      })

      await queryClient.invalidateQueries({
        queryKey: getQuotesQueryKey(prospectId, undefined)
      })

      // Navigate to processing page based on workflow type
      if (workflowType === 'sales') {
        onNavigate(adminQuotingQuotes.benchmarkSuggestions({ prospect_id: prospectId, quote_id: quoteId }))
      } else if (workflowType === 'broker') {
        onNavigate(partnersQuotes.processing({ prospect_id: prospectId, quote_id: quoteId }))
      }
    } catch (error) {
      console.error('Failed to regenerate quote:', error)
    }
  }

  return (
    <Modal open={isOpen}>
      <Modal.Body>
        <Modal.Title dismissible={false}>Updated {quoteYear} prices are available</Modal.Title>
        <Modal.Description>
          We've received new finalized plan data for the states you have employees in. Refresh your quote to get our most up-to-date cost estimate.
        </Modal.Description>
      </Modal.Body>
      <Modal.Footer>
        <div className='flex justify-end'>
          <Modal.Button variant='primary' onClick={handleRegenerateQuote} disabled={generateQuoteMutation.isPending}>
            Refresh quote
          </Modal.Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
